import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';
import { EmptyStateComponent } from '../../../../shared/components/empty-state/empty-state.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { TipoTarifacaoEnum } from '../../../../shared/enums/financas/tipo-tarifacao.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { IntermediadorPagamentoService } from '../../../../shared/services/apis/financas/intermediador-pagamento.service';
import { IntermediadorPagamento } from '../../../../shared/services/models/financas/intermediador-pagamento.model';
import { IntermediadorPagamentoCondicaoComercial } from '../../../../shared/services/models/financas/intermediador-pagamento-condicao-comercial.model';
import { IntermediadorPagamentoRequest } from '../../../../shared/services/requests/financas/intermediador-pagamento.request';
import { IntermediadorPagamentoLookupItem } from '../../../../shared/services/structs/financas/intermediador-pagamento-lookup-item.struct';
import { CodigoSefazEnum } from '../../../../shared/enums/financas/codigo-sefaz.enum';
import { Masks, MaskService } from '../../../../shared/services/mask.service';

interface LookupItem {
  id: number;
  descricao: string;
  codigo?: string | null;
}

const ID_TARIFACAO_EM_DIA_ESPECIFICO = TipoTarifacaoEnum.EmDiaEspecifico;

@Component({
  selector: 'app-intermediador-pagamento-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxMaskDirective,
    NgxCurrencyDirective,
    EmptyStateComponent,
    HeaderComponent,
    LoadingBoxComponent,
    FooterActionComponent,
    PageScrollSpyComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './intermediador-pagamento-cadastro.component.html',
  styleUrl: './intermediador-pagamento-cadastro.component.css',
})
export class IntermediadorPagamentoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

  public form!: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public isEdicao = false;
  public idIntermediadorPagamento = 0;
  public contaFinanceiraDescricao = '';
  public idContaFinanceiraVinculada: number | null = null;

  public masks: Masks = MaskService.getMasks();
  public tarifaMask: any;

  public sections: ScrollSpySection[] = [
    { id: 'informacoes-basicas', label: 'Informações básicas' },
    { id: 'configuracao-tarifas', label: 'Tarifas' },
    { id: 'configuracoes-sped', label: 'Configurações Sped' },
    { id: 'configuracoes-intermediador', label: 'Intermediador' },
    { id: 'condicoes-comerciais', label: 'Condições comerciais' },
  ];

  public adquirentesDisponiveis: LookupItem[] = [];
  public bandeirasDisponiveis: LookupItem[] = [];
  public tiposTarifacaoDisponiveis: LookupItem[] = [];
  public categoriasFinanceirasDisponiveis: LookupItem[] = [];
  public antecipacaoDisponiveis: LookupItem[] = [];
  public tiposRepasseTaxaDisponiveis: LookupItem[] = [];

  public get bandeirasPadraoDisponiveis(): LookupItem[] {
    const selecionadas = new Set(
      ((this.form?.get('bandeiras')?.value as unknown[]) || [])
        .map(value => Number(value))
        .filter(value => Number.isInteger(value) && value > 0)
    );

    return this.bandeirasDisponiveis.filter(item => selecionadas.has(item.id));
  }

  public parcelaOpcoes = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}ª parcela`,
  }));

  public formasDisponiveis: LookupItem[] = [];

  private isPatchingForm = false;

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private intermediadorPagamentoService: IntermediadorPagamentoService,
    private maskService: MaskService
  ) {
    this.tarifaMask = this.maskService.getCurrencyMask();
  }

  getDecimalMask(decimais: number = 2): any {
    return { ...this.maskService.getDecimalMask(decimais), allowNegative: false };
  }

  ngOnInit(): void {
    this.criarForm();
    this.configurarSubscriptions();
    this.carregarLookups();

    this.route.params.subscribe(params => {
      const idParam = params['idIntermediadorPagamento'];
      const id = Number(idParam);
      this.idIntermediadorPagamento = id;
      this.isEdicao = !!idParam && !Number.isNaN(id) && id > 0;

      if (this.isEdicao) {
        this.carregarIntermediador();
      }
    });
  }

  private criarForm(): void {
    this.form = this.fb.group({
      idAdquirente: [null as number | null, Validators.required],
      descricao: ['', [Validators.required]],
      isAtivo: [true],
      idTipoTarifacao: [null as number | null],
      diaDebito: [null as number | null],
      idCategoriaFinanceiraTarifa: [null as number | null],
      cnpjInstituicao: [''],
      enderecoInstituicao: [''],
      municipioInstituicao: [''],
      bandeiras: [[] as number[]],
      idBandeiraPadrao: [null as number | null],
      idAntecipacaoRecebiveis: [null as number | null],
      considerarProximoDiaUtil: [false],
      idTipoRepasseTaxa: [null as number | null],
      repassarTaxasAPartirDe: [null as number | null],
      condicoesComerciais: this.fb.array([]),
    });
  }

  private configurarSubscriptions(): void {
    this.form.get('descricao')?.valueChanges.subscribe((val: string) => {
      if (!this.isEdicao || !this.contaFinanceiraDescricao) {
        this.contaFinanceiraDescricao = (val || '').trim();
      }
    });

    this.form.get('idTipoTarifacao')?.valueChanges.subscribe(() => {
      this.atualizarValidacoesTarifacao();
    });

    this.form.get('bandeiras')?.valueChanges.subscribe(() => {
      this.sincronizarBandeiraPadrao();
      if (!this.isPatchingForm) {
        this.regenerarCondicoesAutomaticas();
      }
    });
  }

  private carregarLookups(): void {
    this.intermediadorPagamentoService.getLookup().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show(
            'Atenção',
            res.errorDescription || 'Erro ao carregar os dados auxiliares.',
            AlertType.warning
          );
          return;
        }

        this.adquirentesDisponiveis = this.mapLookup(res.listaAdquirentes);
        this.bandeirasDisponiveis = this.mapLookup(res.listaBandeiras);
        this.tiposTarifacaoDisponiveis = this.mapLookup(res.listaTiposTarifacao);
        this.categoriasFinanceirasDisponiveis = this.mapLookup(res.listaCategorias);
        this.antecipacaoDisponiveis = this.mapLookup(res.listaAntecipacaoRecebiveis);
        this.tiposRepasseTaxaDisponiveis = this.mapLookup(res.listaTiposRepasseTaxa);
        this.formasDisponiveis = this.mapLookup(res.listaFormasRecebimentoCondicao);
        if (!this.isEdicao) {
          this.regenerarCondicoesAutomaticas();
        }
        this.atualizarValidacoesTarifacao();
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao carregar os dados auxiliares.', AlertType.error);
      },
    });
  }

  private mapLookup(items: IntermediadorPagamentoLookupItem[] | undefined): LookupItem[] {
    return (items || []).map(item => ({
      id: Number(item.id),
      descricao: item.descricao,
      codigo: item.codigo ?? null,
    }));
  }

  public get condicoesComerciaisArray(): FormArray {
    return this.form.get('condicoesComerciais') as FormArray;
  }

  private criarLinhaCondicao(
    idFormaRecebimento: number | null,
    idBandeira: number | null,
    parceladoAte: number,
    prazo: number,
    isAutoGerado: boolean = false,
    taxa: number | null = null,
    tarifa: number | null = null
  ): FormGroup {
    return this.fb.group({
      idFormaRecebimento: [idFormaRecebimento, Validators.required],
      idBandeira: [idBandeira],
      parceladoAte: [parceladoAte, [Validators.required, Validators.min(1), Validators.max(12)]],
      prazo: [prazo, [Validators.required, Validators.min(0)]],
      taxa: [taxa],
      tarifa: [tarifa],
      isAutoGerado: [isAutoGerado],
    });
  }

  public adicionarCondicaoManual(): void {
    const idFormaRecebimento = this.formasDisponiveis[0]?.id ?? null;
    this.condicoesComerciaisArray.push(
      this.criarLinhaCondicao(
        idFormaRecebimento,
        null,
        1,
        this.getPrazoDefaultByFormaId(idFormaRecebimento),
        false
      )
    );
  }

  public removerCondicao(index: number): void {
    this.condicoesComerciaisArray.removeAt(index);
  }

  private regenerarCondicoesAutomaticas(): void {
    const array = this.condicoesComerciaisArray;

    for (let i = array.length - 1; i >= 0; i--) {
      if ((array.at(i) as FormGroup).get('isAutoGerado')?.value === true) {
        array.removeAt(i);
      }
    }

    [this.getFormaCartaoDebito(), this.getFormaBoleto(), this.getFormaPix()]
      .filter((forma): forma is LookupItem => !!forma)
      .forEach(forma => {
        array.push(this.criarLinhaCondicao(forma.id, null, 1, this.getPrazoDefaultByFormaId(forma.id), true));
      });

    const formaCartaoCredito = this.getFormaCartaoCredito();
    if (!formaCartaoCredito) {
      return;
    }

    const selectedIds: number[] = (this.form.get('bandeiras')?.value || []).map((value: unknown) =>
      Number(value)
    );
    selectedIds.forEach(bandeiraId => {
      if (!bandeiraId) {
        return;
      }

      for (let parcela = 1; parcela <= 12; parcela++) {
        array.push(
          this.criarLinhaCondicao(
            formaCartaoCredito.id,
            bandeiraId,
            parcela,
            this.getPrazoDefaultByFormaId(formaCartaoCredito.id),
            true
          )
        );
      }
    });
  }

  public onFormaCondicaoChange(index: number): void {
    const row = this.condicoesComerciaisArray.at(index) as FormGroup;
    const idFormaRecebimento = this.parseNullablePositiveInt(row.get('idFormaRecebimento')?.value);

    row.get('prazo')?.setValue(this.getPrazoDefaultByFormaId(idFormaRecebimento), { emitEvent: false });

    if (!this.aceitaBandeira(idFormaRecebimento)) {
      row.get('idBandeira')?.setValue(null, { emitEvent: false });
    }
  }

  public isEmDiaEspecifico(): boolean {
    return Number(this.form.get('idTipoTarifacao')?.value) === ID_TARIFACAO_EM_DIA_ESPECIFICO;
  }

  public exibeCategoriaFinanceira(): boolean {
    return Number(this.form.get('idTipoTarifacao')?.value) > 0;
  }

  private atualizarValidacoesTarifacao(): void {
    const diaControl = this.form.get('diaDebito') as AbstractControl;

    if (this.isEmDiaEspecifico()) {
      diaControl.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(31),
        Validators.pattern(/^\d+$/),
      ]);
    } else {
      diaControl.clearValidators();
      diaControl.setValue(null, { emitEvent: false });
    }

    diaControl.updateValueAndValidity({ emitEvent: false });
  }

  private carregarIntermediador(): void {
    this.isLoading = true;
    this.intermediadorPagamentoService.getById(this.idIntermediadorPagamento).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.isError) {
          this.alertService.show(
            'Atenção',
            res.errorDescription || 'Erro ao carregar intermediador.',
            AlertType.warning
          );
          this.voltar();
          return;
        }

        if (!res.intermediador) {
          this.alertService.show('Atenção', 'Intermediador de pagamento não encontrado.', AlertType.warning);
          this.voltar();
          return;
        }

        this.preencherFormulario(res.intermediador);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar intermediador.', AlertType.error);
        this.voltar();
      },
    });
  }

  private preencherFormulario(intermediador: IntermediadorPagamento): void {
    this.isPatchingForm = true;

    this.form.patchValue(
      {
        idAdquirente: this.parseNullablePositiveInt(intermediador.idAdquirente),
        descricao: intermediador.descricao || '',
        isAtivo: !!intermediador.isAtivo,
        idTipoTarifacao: this.parseNullablePositiveInt(intermediador.idTipoTarifacao),
        diaDebito: this.parseNullablePositiveInt(intermediador.diaDebito),
        idCategoriaFinanceiraTarifa: this.parseNullablePositiveInt(intermediador.idCategoriaFinanceiraTarifa),
        cnpjInstituicao: intermediador.cnpjInstituicao || '',
        enderecoInstituicao: intermediador.enderecoInstituicao || '',
        municipioInstituicao: intermediador.municipioInstituicao || '',
        bandeiras: (intermediador.bandeiras || []).map(value => Number(value)),
        idBandeiraPadrao: this.parseNullablePositiveInt(intermediador.idBandeiraPadrao),
        idAntecipacaoRecebiveis: this.parseNullablePositiveInt(intermediador.idAntecipacaoRecebiveis),
        considerarProximoDiaUtil: !!intermediador.considerarProximoDiaUtil,
        idTipoRepasseTaxa: this.parseNullablePositiveInt(intermediador.idTipoRepasseTaxa),
        repassarTaxasAPartirDe: this.parseNullablePositiveInt(intermediador.repassarTaxasAPartirDe),
      },
      { emitEvent: false }
    );

    this.contaFinanceiraDescricao = (
      intermediador.descricaoContaFinanceira ||
      intermediador.descricao ||
      ''
    ).trim();
    this.idContaFinanceiraVinculada = this.parseNullablePositiveInt(intermediador.idContaFinanceira);
    this.popularCondicoesComerciais(intermediador.condicoesComerciais || []);
    this.isPatchingForm = false;
    this.sincronizarBandeiraPadrao();
    this.atualizarValidacoesTarifacao();
  }

  private sincronizarBandeiraPadrao(): void {
    const control = this.form.get('idBandeiraPadrao');
    const selecionadas = ((this.form.get('bandeiras')?.value as unknown[]) || [])
      .map(value => Number(value))
      .filter(value => Number.isInteger(value) && value > 0);

    const atual = this.parseNullablePositiveInt(control?.value);

    if (!selecionadas.length) {
      control?.setValue(null, { emitEvent: false });
      return;
    }

    if (atual && selecionadas.includes(atual)) {
      return;
    }

    control?.setValue(selecionadas[0], { emitEvent: false });
  }

  private popularCondicoesComerciais(condicoes: IntermediadorPagamentoCondicaoComercial[]): void {
    this.condicoesComerciaisArray.clear();

    condicoes.forEach(condicao => {
      this.condicoesComerciaisArray.push(
        this.criarLinhaCondicao(
          this.parseNullablePositiveInt(condicao.idFormaRecebimento),
          this.parseNullablePositiveInt(condicao.idBandeira),
          Number(condicao.parceladoAte) || 1,
          Number(condicao.prazo) || 0,
          this.isCondicaoAutoGerada(condicao),
          this.parseNullableNumber(condicao.taxa),
          this.parseNullableNumber(condicao.tarifa)
        )
      );
    });
  }

  private isCondicaoAutoGerada(condicao: IntermediadorPagamentoCondicaoComercial): boolean {
    const idFormaRecebimento = this.parseNullablePositiveInt(condicao.idFormaRecebimento);
    if (!idFormaRecebimento) {
      return false;
    }

    if (this.isFormaCartaoCredito(idFormaRecebimento)) {
      return (
        !!this.parseNullablePositiveInt(condicao.idBandeira) &&
        Number(condicao.parceladoAte) >= 1 &&
        Number(condicao.parceladoAte) <= 12
      );
    }

    return (
      this.isFormaCondicaoBasica(idFormaRecebimento) && !this.parseNullablePositiveInt(condicao.idBandeira)
    );
  }

  public getFormaCondicaoLabel(value: unknown): string {
    const idFormaRecebimento = this.parseNullablePositiveInt(value);
    if (!idFormaRecebimento) {
      return '-';
    }

    return this.formasDisponiveis.find(item => item.id === idFormaRecebimento)?.descricao || '-';
  }

  public getBandeiraCondicaoLabel(value: unknown): string {
    const idBandeira = this.parseNullablePositiveInt(value);
    if (!idBandeira) {
      return '-';
    }

    return this.bandeirasDisponiveis.find(item => item.id === idBandeira)?.descricao || '-';
  }

  public getParceladoCondicaoLabel(forma: unknown, parceladoAte: unknown): string {
    const numeroParcela = Number(parceladoAte);
    if (!Number.isFinite(numeroParcela) || numeroParcela <= 0) {
      return '-';
    }

    const idFormaRecebimento = this.parseNullablePositiveInt(forma);
    if (idFormaRecebimento && this.isFormaCartaoCredito(idFormaRecebimento)) {
      return `${numeroParcela}x`;
    }

    return '-';
  }

  private normalizarTexto(value: string | null | undefined): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  public parseNullablePositiveInt(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null;
  }

  private parseNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(String(value).replace(',', '.').trim());
    return Number.isNaN(parsed) ? null : parsed;
  }

  private sanitizeNullableText(value: unknown): string | null {
    const text = (value || '').toString().trim();
    return text ? text : null;
  }

  private sanitizeNullableCnpj(value: unknown): string | null {
    const digits = (value || '').toString().replace(/\D/g, '');
    return digits ? digits : null;
  }

  private getFormaByCodigo(codigo: string): LookupItem | undefined {
    return this.formasDisponiveis.find(item => item.codigo === codigo);
  }

  private getFormaCartaoCredito(): LookupItem | undefined {
    return this.getFormaByCodigo(CodigoSefazEnum.CartaoCredito);
  }

  private getFormaCartaoDebito(): LookupItem | undefined {
    return this.getFormaByCodigo(CodigoSefazEnum.CartaoDebito);
  }

  private getFormaBoleto(): LookupItem | undefined {
    return this.getFormaByCodigo(CodigoSefazEnum.Boleto);
  }

  private getFormaPix(): LookupItem | undefined {
    return this.getFormaByCodigo(CodigoSefazEnum.Pix);
  }

  private getPrazoDefaultByFormaId(idFormaRecebimento: number | null): number {
    return idFormaRecebimento && this.isFormaCartaoCredito(idFormaRecebimento) ? 31 : 2;
  }

  private isFormaCartaoCredito(idFormaRecebimento: number): boolean {
    return this.getFormaCartaoCredito()?.id === idFormaRecebimento;
  }

  private isFormaCondicaoBasica(idFormaRecebimento: number): boolean {
    return [this.getFormaCartaoDebito()?.id, this.getFormaBoleto()?.id, this.getFormaPix()?.id]
      .filter((value): value is number => !!value)
      .includes(idFormaRecebimento);
  }

  public aceitaBandeira(idFormaRecebimento: number | null): boolean {
    return !!idFormaRecebimento && this.isFormaCartaoCredito(idFormaRecebimento);
  }

  public salvar(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const value = this.form.getRawValue();

    const request: IntermediadorPagamentoRequest = {
      idAdquirente: Number(value.idAdquirente),
      descricao: (value.descricao || '').trim(),
      isAtivo: !!value.isAtivo,
      idTipoTarifacao: this.parseNullablePositiveInt(value.idTipoTarifacao),
      diaDebito: this.isEmDiaEspecifico() ? this.parseNullablePositiveInt(value.diaDebito) : null,
      idCategoriaFinanceiraTarifa: this.exibeCategoriaFinanceira()
        ? this.parseNullablePositiveInt(value.idCategoriaFinanceiraTarifa)
        : null,
      cnpjInstituicao: this.sanitizeNullableCnpj(value.cnpjInstituicao),
      enderecoInstituicao: this.sanitizeNullableText(value.enderecoInstituicao),
      municipioInstituicao: this.sanitizeNullableText(value.municipioInstituicao),
      bandeiras: (value.bandeiras || [])
        .map((bandeiraId: unknown) => Number(bandeiraId))
        .filter((bandeiraId: number) => Number.isInteger(bandeiraId) && bandeiraId > 0),
      idBandeiraPadrao: this.parseNullablePositiveInt(value.idBandeiraPadrao),
      idAntecipacaoRecebiveis: this.parseNullablePositiveInt(value.idAntecipacaoRecebiveis),
      considerarProximoDiaUtil: !!value.considerarProximoDiaUtil,
      idTipoRepasseTaxa: this.parseNullablePositiveInt(value.idTipoRepasseTaxa),
      repassarTaxasAPartirDe: this.parseNullablePositiveInt(value.repassarTaxasAPartirDe),
      condicoesComerciais: (value.condicoesComerciais || []).map((condicao: any) => ({
        idFormaRecebimento: this.parseNullablePositiveInt(condicao.idFormaRecebimento) ?? 0,
        idBandeira: this.parseNullablePositiveInt(condicao.idBandeira),
        parceladoAte: Number(condicao.parceladoAte),
        prazo: Number(condicao.prazo),
        taxa: this.parseNullableNumber(condicao.taxa),
        tarifa: this.parseNullableNumber(condicao.tarifa),
      })),
    };

    if (request.condicoesComerciais.some(c => c.idFormaRecebimento <= 0)) {
      this.isSaving = false;
      this.alertService.show(
        'Atenção',
        'Selecione uma forma válida para todas as condições comerciais.',
        AlertType.warning
      );
      return;
    }

    if (request.idBandeiraPadrao && !request.bandeiras.includes(request.idBandeiraPadrao)) {
      this.isSaving = false;
      this.alertService.show(
        'Atenção',
        'A bandeira padrão deve estar entre as bandeiras atendidas.',
        AlertType.warning
      );
      return;
    }

    const salvar$ = this.isEdicao
      ? this.intermediadorPagamentoService.put(this.idIntermediadorPagamento, request)
      : this.intermediadorPagamentoService.post(request);

    salvar$.subscribe({
      next: res => {
        this.isSaving = false;

        if (res.isError) {
          this.alertService.show(
            'Atenção',
            res.errorDescription || 'Erro ao salvar intermediador.',
            AlertType.warning
          );
          return;
        }

        this.alertService.show(
          'Sucesso',
          `Intermediador ${this.isEdicao ? 'atualizado' : 'cadastrado'} com sucesso.`,
          AlertType.success
        );
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Erro ao salvar intermediador.', AlertType.error);
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/financas/intermediador-pagamento']);
  }
}
